if (!customElements.get('you-viewed-products')) {

  class YouViewedProducts extends HTMLElement {
    observer = undefined;
    STORAGE_KEY = 'shopify_viewed_products';
    MAX_STORED = 50; // Maximum products to store in localStorage

    constructor() {
      super();
    }

    connectedCallback() {
      // Load and display viewed products
      this.initializeViewedProducts();
    }

    hideSection() {
      const section = this.closest('section');
      if (section) section.style.display = 'none';
    }

    getViewedProducts() {
      try {
        const stored = localStorage.getItem(this.STORAGE_KEY);
        return stored ? JSON.parse(stored) : [];
      } catch (e) {
        console.error('Error reading viewed products:', e);
        return [];
      }
    }

    initializeViewedProducts() {
      this.observer?.unobserve(this);
      this.observer = new IntersectionObserver(
        (entries, observer) => {
          if (!entries[0].isIntersecting) return;
          observer.unobserve(this);
          this.loadViewedProducts();
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      this.observer.observe(this);
    }

    async loadViewedProducts() {
      const viewedProducts = this.getViewedProducts();
      const currentProductHandle = this.dataset.currentProductHandle;
      const productsToShow = parseInt(this.dataset.productsToShow) || 4;

      const productHandles = viewedProducts
        .filter((p) => p.handle && p.handle !== currentProductHandle)
        .map((p) => p.handle);

      const uniqueHandles = [...new Set(productHandles)].slice(0, productsToShow);

      if (!uniqueHandles.length) {
        this.hideSection();
        return;
      }

      try {
        const products = await this.fetchProductsJson(uniqueHandles);
        if (!products.length) {
          this.hideSection();
          return;
        }

        this.renderProductsFromJson(products);
      } catch (e) {
        console.error('Error loading viewed products:', e);
      }
    }

    async fetchProductsJson(handles) {
      const results = [];

      for (const handle of handles) {
        try {
          const url = `/products/${handle}.js`;
          const response = await fetch(url);
          if (!response.ok) {
            console.warn('Failed to fetch product JSON for', handle, response.status);
            continue;
          }
          const data = await response.json();
          if (data && data.handle) {
            results.push(data);
          }
        } catch (e) {
          console.warn('Error fetching product JSON for', handle, e);
        }
      }

      return results;
    }

    formatPrice(price) {
      if (typeof price !== 'number') return '';
      const money = price / 100;
      try {
        const currency =
          (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
          (window.Shopify && window.Shopify.currency && window.Shopify.currency.current) ||
          '';
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: currency || 'USD',
        }).format(money);
      } catch (e) {
        return `$${money.toFixed(2)}`;
      }
    }

    renderProductsFromJson(products) {
      const grid = this.querySelector('.product-grid');
      const heading = this.querySelector('.related-products__heading');
      const showSecondaryImage = this.dataset.showSecondaryImage === 'true';
      const showColorSwatches = this.dataset.showColorSwatches === 'true';

      if (!grid || !heading) {
        console.error('Missing grid or heading element', { grid, heading });
        return;
      }

      grid.innerHTML = '';

      products.forEach((product, index) => {

        const li = document.createElement('li');
        li.className = 'grid__item you-viewed-product-card-item';

        // Get price - Shopify JSON returns price in cents
        const priceInCents = product.price_min || product.price || 0;
        const compareAtPrice = product.compare_at_price || null;
        const priceFormatted = this.formatPrice(priceInCents);
        const compareAtPriceFormatted = compareAtPrice ? this.formatPrice(compareAtPrice) : null;

        // Get featured image - Shopify JSON structure
        // Shopify JSON returns: featured_image as string URL or {src, alt, width, height}
        let featuredImage = null;
        let imageSrc = null;
        let imageWidth = 533;
        let imageHeight = 533;
        let imageAlt = product.title || '';

        if (product.featured_image) {
          if (typeof product.featured_image === 'string') {
            imageSrc = product.featured_image;
            featuredImage = { src: imageSrc };
          } else if (product.featured_image.src) {
            imageSrc = product.featured_image.src;
            imageWidth = product.featured_image.width || 533;
            imageHeight = product.featured_image.height || 533;
            imageAlt = product.featured_image.alt || product.title || '';
            featuredImage = product.featured_image;
          }
        } else if (product.images && product.images.length > 0) {
          const firstImage = product.images[0];
          if (typeof firstImage === 'string') {
            imageSrc = firstImage;
            featuredImage = { src: imageSrc };
          } else if (firstImage.src) {
            imageSrc = firstImage.src;
            imageWidth = firstImage.width || 533;
            imageHeight = firstImage.height || 533;
            imageAlt = firstImage.alt || product.title || '';
            featuredImage = firstImage;
          }
        }
        
        // Build image srcset for responsive images
        // Shopify image URLs can be formatted with size parameters
        let imageHtml = '';
        if (imageSrc) {
          const buildImageSrcset = (imgSrc) => {
            let baseImageUrl = imgSrc;
            const sizeMatch = imgSrc.match(/_(?:(\d+)x(\d+)|(\d+)x)(\.(jpg|jpeg|png|webp|gif))?$/i);
            if (sizeMatch) {
              baseImageUrl = imgSrc.replace(/_\d+x\d*\.(jpg|jpeg|png|webp|gif)$/i, '');
            }
            const ext = imgSrc.match(/\.(jpg|jpeg|png|webp|gif)$/i)?.[0] || '.jpg';
            const baseWithoutExt = baseImageUrl.replace(/\.(jpg|jpeg|png|webp|gif)$/i, '');
            return [
              `${baseWithoutExt}_165x${ext} 165w`,
              `${baseWithoutExt}_360x${ext} 360w`,
              `${baseWithoutExt}_533x${ext} 533w`,
              `${baseWithoutExt}_720x${ext} 720w`,
              `${baseWithoutExt}_940x${ext} 940w`,
              `${baseWithoutExt}_1066x${ext} 1066w`,
              `${baseImageUrl} ${imageWidth}w`
            ].join(', ');
          };

          const ext = imageSrc.match(/\.(jpg|jpeg|png|webp|gif)$/i)?.[0] || '.jpg';
          const baseImageUrl = imageSrc.replace(/_\d+x\d*\.(jpg|jpeg|png|webp|gif)$/i, '').replace(/\.(jpg|jpeg|png|webp|gif)$/i, '');
          const srcset = buildImageSrcset(imageSrc);

          // Second image for hover (when show_secondary_image is enabled)
          let secondImageHtml = '';
          if (showSecondaryImage && product.images && product.images.length > 1) {
            const secondImg = product.images[1];
            const secondSrc = typeof secondImg === 'string' ? secondImg : (secondImg && secondImg.src);
            if (secondSrc) {
              const secondBase = secondSrc.replace(/_\d+x\d*\.(jpg|jpeg|png|webp|gif)$/i, '').replace(/\.(jpg|jpeg|png|webp|gif)$/i, '');
              const secondExt = secondSrc.match(/\.(jpg|jpeg|png|webp|gif)$/i)?.[0] || '.jpg';
              const secondSrcset = buildImageSrcset(secondSrc);
              const secondAlt = typeof secondImg === 'object' && secondImg.alt ? secondImg.alt : (product.title || '');
              secondImageHtml = `
                <img
                  srcset="${secondSrcset}"
                  src="${secondBase}_533x${secondExt}"
                  sizes="(min-width: 990px) calc((100vw - 130px) / 4), (min-width: 750px) calc((100vw - 120px) / 3), calc((100vw - 35px) / 2)"
                  alt="${this.escapeHtml(secondAlt)}"
                  class="motion-reduce"
                  loading="lazy"
                  width="${typeof secondImg === 'object' && secondImg.width ? secondImg.width : 533}"
                  height="${typeof secondImg === 'object' && secondImg.height ? secondImg.height : 533}"
                />`;
            }
          }

          imageHtml = `
            <div class="card__media">
              <div class="media media--transparent media--hover-effect">
                <img
                  srcset="${srcset}"
                  src="${baseImageUrl}_533x${ext}"
                  sizes="(min-width: 990px) calc((100vw - 130px) / 4), (min-width: 750px) calc((100vw - 120px) / 3), calc((100vw - 35px) / 2)"
                  alt="${this.escapeHtml(imageAlt)}"
                  class="motion-reduce"
                  loading="lazy"
                  width="${imageWidth}"
                  height="${imageHeight}"
                />
                ${secondImageHtml}
              </div>
            </div>`;

          if (showColorSwatches && product.variants && product.variants.length > 1 && Array.isArray(product.options)) {
            const colorOptionIndex = product.options.findIndex((name) => {
              const n = String(name || '').toLowerCase();
              return n === 'color' || n === 'colour';
            });
            if (colorOptionIndex >= 0) {
              const uniqueColors = [];
              const seen = new Set();
              product.variants.forEach((variant) => {
                const variantOptions = Array.isArray(variant.options) ? variant.options : [variant.option1, variant.option2, variant.option3];
                const colorValue = variantOptions[colorOptionIndex];
                if (!colorValue || seen.has(colorValue)) return;
                seen.add(colorValue);
                uniqueColors.push({ variant, colorValue });
              });

              if (uniqueColors.length > 1) {
                const normalize = (src) => (src || '').replace(/_\d+x\d*\.(jpg|jpeg|png|webp|gif)$/i, '').replace(/\.(jpg|jpeg|png|webp|gif)$/i, '');
                const to940 = (src) => {
                  if (!src) return '';
                  const ext = src.match(/\.(jpg|jpeg|png|webp|gif)$/i)?.[0] || '.jpg';
                  const base = normalize(src);
                  return `${base}_940x${ext}`;
                };

                const swatchButtons = uniqueColors.map(({ variant, colorValue }, idx) => {
                  const variantImageSrc = (variant.featured_image && variant.featured_image.src) || imageSrc;
                  const swatchImageUrl = to940(variantImageSrc);
                  let swatchSecondaryImage = '';
                  if (showSecondaryImage && Array.isArray(product.images) && product.images.length > 1) {
                    const fallback = product.images.find((img) => normalize(img) !== normalize(variantImageSrc));
                    if (fallback) swatchSecondaryImage = to940(fallback);
                  }
                  const swatchColor = String(colorValue).toLowerCase() === 'athletic heather' ? '#b4b5b2' : String(colorValue).toLowerCase();
                  return `<button type="button" class="you-viewed-section-${this.dataset.sectionId}__swatch${idx === 0 ? ` you-viewed-section-${this.dataset.sectionId}__swatch--active` : ''}" data-variant-image="${this.escapeHtml(swatchImageUrl)}" data-variant-secondary-image="${this.escapeHtml(swatchSecondaryImage)}" data-variant-price="${this.escapeHtml(this.formatPrice(variant.price || priceInCents))}" data-variant-id="${variant.id || ''}" data-variant-alt="${this.escapeHtml((product.title || '') + ' - ' + colorValue)}" style="background-color: ${this.escapeHtml(swatchColor)};" aria-label="${this.escapeHtml(colorValue)}"></button>`;
                });

                const swatchesHtml = `<div class="you-viewed-section-${this.dataset.sectionId}__swatches">${swatchButtons.join('')}</div>`;
                imageHtml = imageHtml.replace(/<\/div>\s*<\/div>\s*$/, `${swatchesHtml}</div></div>`);
              }
            }
          }
        }

        // Build price HTML - match price snippet structure
        let priceHtml = '';
        if (priceFormatted) {
          const hasSale = compareAtPriceFormatted && compareAtPriceFormatted !== priceFormatted;
          priceHtml = `
            <div class="card-information">
              <div class="price ${hasSale ? 'price--on-sale' : ''}">
                <div class="price__container">
                  ${hasSale ? `
                    <div class="price__sale">
                      <span class="visually-hidden visually-hidden--inline">Regular price</span>
                      <span>
                        <s class="price-item price-item--regular">${compareAtPriceFormatted}</s>
                      </span>
                      <span class="visually-hidden visually-hidden--inline">Sale price</span>
                      <span class="price-item price-item--sale price-item--last">${priceFormatted}</span>
                    </div>
                  ` : `
                    <div class="price__regular">
                      <span class="visually-hidden visually-hidden--inline">Regular price</span>
                      <span class="price-item price-item--regular">${priceFormatted}</span>
                    </div>
                  `}
                </div>
              </div>
            </div>`;
        }

        // Build the card structure matching card-product.liquid exactly
        // First card__content inside card__inner (for no-media case)
        const firstCardContent = `
          <div class="card__content">
            <div class="card__information">
              <h3 class="card__heading" id="StandardCardNoMediaLink-${product.id}">
                <a href="${product.url}" id="StandardCardNoMediaLink-${product.id}" class="full-unstyled-link" aria-labelledby="StandardCardNoMediaLink-${product.id}">
                  ${this.escapeHtml(product.title || '')}
                </a>
              </h3>
            </div>
          </div>`;

        // Second card__content as sibling to card__inner (main content with price)
        const secondCardContent = `
          <div class="card__content">
            <div class="card__information">
              <h3 class="card__heading h5" id="title-${product.id}">
                <a href="${product.url}" id="CardLink-${product.id}" class="full-unstyled-link" aria-labelledby="CardLink-${product.id}">
                  ${this.escapeHtml(product.title || '')}
                </a>
              </h3>
              ${priceHtml}
            </div>
            <div class="card__badge bottom left"></div>
          </div>`;

        li.innerHTML = `
          <div class="card-wrapper product-card-wrapper underline-links-hover">
            <div class="card card--${this.getCardStyle()} card--media">
              <div class="card__inner color-${this.getColorScheme()} gradient ratio">
                ${imageHtml}
                ${firstCardContent}
              </div>
              ${secondCardContent}
            </div>
          </div>
        `;

        grid.appendChild(li);
      });

      if (grid.children.length) {
        grid.style.display = '';
        heading.style.display = '';
        this.classList.add('you-viewed-products--loaded');
        this.initSliderProgressBar();
        this.initSwatchBehavior();
      } else {
        this.hideSection();
      }
    }

    initSwatchBehavior() {
      const sectionId = this.dataset.sectionId;
      const cards = this.querySelectorAll('.you-viewed-product-card-item');
      cards.forEach((card) => {
        const primaryLink = card.querySelector('.card__heading a.full-unstyled-link');
        card.addEventListener('click', (event) => {
          if (!primaryLink || !primaryLink.href) return;
          if (
            event.target.closest('.you-viewed-section-' + sectionId + '__swatches') ||
            event.target.closest('button') ||
            event.target.closest('a') ||
            event.target.closest('input') ||
            event.target.closest('select') ||
            event.target.closest('textarea') ||
            event.target.closest('label')
          ) return;
          window.location.href = primaryLink.href;
        });

        const swatches = card.querySelectorAll('.you-viewed-section-' + sectionId + '__swatch');
        if (!swatches.length) return;
        const swatchContainer = card.querySelector('.you-viewed-section-' + sectionId + '__swatches');
        const mediaContainer = card.querySelector('.card__media');
        if (swatchContainer && mediaContainer) mediaContainer.appendChild(swatchContainer);

        const mainImage = card.querySelector('.card__media .media img');
        const secondaryImage = card.querySelector('.card__media .media img + img');
        const productLinks = card.querySelectorAll('.full-unstyled-link');
        const regularPrice = card.querySelector('.price-item--regular');
        const salePrice = card.querySelector('.price-item--sale');
        if (!mainImage) return;

        swatches.forEach((swatch) => {
          ['mousedown', 'touchstart'].forEach((evtName) => {
            swatch.addEventListener(evtName, (event) => { event.stopPropagation(); }, true);
          });
          swatch.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            const imageUrl = swatch.dataset.variantImage;
            const secondaryImageUrl = swatch.dataset.variantSecondaryImage;
            const price = swatch.dataset.variantPrice;
            const variantId = swatch.dataset.variantId;
            const alt = swatch.dataset.variantAlt;
            if (imageUrl) { mainImage.src = imageUrl; mainImage.removeAttribute('srcset'); }
            if (secondaryImage && secondaryImageUrl) { secondaryImage.src = secondaryImageUrl; secondaryImage.removeAttribute('srcset'); }
            if (alt) { mainImage.alt = alt; if (secondaryImage) secondaryImage.alt = alt; }
            if (price) { if (salePrice) salePrice.textContent = price; else if (regularPrice) regularPrice.textContent = price; }
            if (variantId && productLinks.length) {
              productLinks.forEach((linkEl) => {
                if (!linkEl.href) return;
                const baseUrl = linkEl.href.split('?')[0];
                linkEl.href = baseUrl + '?variant=' + variantId;
              });
            }
            swatches.forEach((item) => item.classList.remove('you-viewed-section-' + sectionId + '__swatch--active'));
            swatch.classList.add('you-viewed-section-' + sectionId + '__swatch--active');
          });
        });
      });
    }

    initSliderProgressBar() {
      const grid = this.querySelector('[data-slider-track]');
      const progressTrack = this.querySelector('[data-slider-progress-track]');
      const sectionId = this.dataset.sectionId;
      if (!grid || !progressTrack || !sectionId) return;

      const productsToShow = parseInt(this.dataset.productsToShow) || 4;
      const prefix = 'you-viewed-section-' + sectionId;

      const initSegments = () => {
        const items = grid.querySelectorAll('.grid__item');
        const segmentCount = Math.min(productsToShow, items.length);
        progressTrack.innerHTML = '';
        for (let i = 0; i < segmentCount; i++) {
          const seg = document.createElement('div');
          seg.className = prefix + '__slider-progress-segment';
          seg.setAttribute('data-slider-progress-segment', i);
          progressTrack.appendChild(seg);
        }
      };

      const updateProgressBar = () => {
        const items = Array.from(grid.querySelectorAll('.grid__item'));
        const count = Math.min(productsToShow, items.length);
        if (count === 0) return;
        let activeIndex = 0;
        let minDist = Infinity;
        for (let i = 0; i < count; i++) {
          const dist = Math.abs(items[i].offsetLeft - grid.scrollLeft);
          if (dist < minDist) {
            minDist = dist;
            activeIndex = i;
          }
        }
        const segments = progressTrack.querySelectorAll('[data-slider-progress-segment]');
        segments.forEach((seg, j) => {
          seg.classList.toggle(prefix + '__slider-progress-segment--active', j === activeIndex);
        });
      };

      initSegments();
      grid.addEventListener('scroll', updateProgressBar);
      window.addEventListener('resize', () => {
        updateProgressBar();
        if (progressTrack.children.length === 0) initSegments();
      });
      updateProgressBar();
    }

    getCardStyle() {
      // Try to get card style from theme settings, default to 'standard'
      try {
        const cardStyle = document.documentElement.style.getPropertyValue('--card-style') || 
                         (window.theme && window.theme.settings && window.theme.settings.card_style) ||
                         'standard';
        return cardStyle;
      } catch (e) {
        return 'standard';
      }
    }

    getColorScheme() {
      // Try to get color scheme from parent element
      const parent = this.closest('.color-scheme-1, .color-scheme-2, [class*="color-"]');
      if (parent) {
        const match = parent.className.match(/color-([^\s]+)/);
        if (match) return match[1];
      }
      return 'scheme-1';
    }

    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }
  }

  customElements.define('you-viewed-products', YouViewedProducts);
}

// Track product views on product pages
(function() {
  function trackProductView() {
    // Try to get product handle from URL
    const pathMatch = window.location.pathname.match(/\/products\/([^\/\?]+)/);
    if (!pathMatch) {
      return;
    }
    
    const productHandle = pathMatch[1];
    if (!productHandle) {
      return;
    }


    try {
      const STORAGE_KEY = 'shopify_viewed_products';
      const MAX_STORED = 50;
      const viewedProducts = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      
      // Remove current product if it exists
      const filtered = viewedProducts.filter(p => p.handle !== productHandle);
      
      // Add current product to the beginning
      filtered.unshift({ handle: productHandle, viewedAt: Date.now() });
      
      // Keep only the most recent products
      const limited = filtered.slice(0, MAX_STORED);
      
      localStorage.setItem(STORAGE_KEY, JSON.stringify(limited));
    } catch (e) {
      console.error('Error tracking product view:', e);
    }
  }

  // Track on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', trackProductView);
  } else {
    trackProductView();
  }
})();
